import React from 'react';
import { Menu } from '@ygtechnologies/rubus-ui';
import { Link, useRouteMatch } from 'react-router-dom';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faUsers,
    faSignOut,
    faBuilding,
    faUsersCrown,
    faChartArea,
    faChartLine,
    faQuestionCircle,
    faFileInvoiceDollar,
    faUserPlus,
    faClipboardListCheck,
    faCog,
    faEnvelope,
    faFileCode,
    faMoon,
    faSun,
    faHeartPulse,
} from '@fortawesome/pro-light-svg-icons';
import { hasAccessLevel, MacGyverAccessLevel } from '@/services/auth';
import { useThemePreference } from '@/components/ThemePreferenceContext';

interface SidebarProps {
    accessLevel: MacGyverAccessLevel;
    collapsed: boolean;
    onCollapsedChange(collapsed: boolean): void;
}

interface SidebarItemProps {
    to: string;
    icon: IconDefinition;
    children: React.ReactNode;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon, children }) => {
    // match on the first path segment so e.g. /invitations/buyer keeps the Invitations item active
    const match = useRouteMatch(`/${to.split('/')[1]}`);
    return (
        <Menu.Item as={Link} to={to} icon={icon} active={!!match}>
            {children}
        </Menu.Item>
    );
};

const Sidebar: React.FC<SidebarProps> = ({ accessLevel, collapsed, onCollapsedChange }) => {
    const { themePreference, setThemePreference } = useThemePreference();
    const isDark = themePreference === 'dark';

    return (
        <Menu mode="sidebar" collapsible collapsed={collapsed} onCollapsedChange={onCollapsedChange}>
            <Menu.Section type="primary">
                {hasAccessLevel(accessLevel, 'Advanced') && (
                    <SidebarItem to="/system-health/health" icon={faHeartPulse}>
                        System health
                    </SidebarItem>
                )}
                <SidebarItem to="/statistics" icon={faChartArea}>
                    Statistics
                </SidebarItem>
                <SidebarItem to="/over-time-statistics" icon={faChartLine}>
                    Over Time
                </SidebarItem>
            </Menu.Section>

            <Menu.Section type="default">
                <SidebarItem to="/users" icon={faUsers}>
                    Users
                </SidebarItem>
                <SidebarItem to="/projects" icon={faBuilding}>
                    Projects
                </SidebarItem>
                <SidebarItem to="/profiles" icon={faUsersCrown}>
                    Profiles
                </SidebarItem>
                <SidebarItem to="/invitations/profile" icon={faUserPlus}>
                    Invitations
                </SidebarItem>

                <SidebarItem to="/licenses" icon={faFileInvoiceDollar}>
                    Licenses
                </SidebarItem>
                <SidebarItem to="/features" icon={faClipboardListCheck}>
                    Features
                </SidebarItem>

                {hasAccessLevel(accessLevel, 'Advanced') && (
                    <>
                        <Menu.Divider />
                        <SidebarItem to="/communicore/outgoing-emails" icon={faEnvelope}>
                            Communicore
                        </SidebarItem>
                        <SidebarItem to="/email-templates/contents" icon={faFileCode}>
                            Email templates
                        </SidebarItem>
                    </>
                )}
                {/* <SidebarItem to="/electricity-suppliers" icon={faPlug}>Electricity Suppliers</SidebarItem> */}
            </Menu.Section>
            <Menu.Pusher />
            <Menu.Section type="secondary">
                <Menu.Item icon={isDark ? faSun : faMoon} onClick={() => setThemePreference(isDark ? 'light' : 'dark')}>
                    {isDark ? 'Light mode' : 'Dark mode'}
                </Menu.Item>
                {accessLevel === 'Admin' && (
                    <SidebarItem to="/settings/users" icon={faCog}>
                        Settings
                    </SidebarItem>
                )}
                <SidebarItem to="/about" icon={faQuestionCircle}>
                    About
                </SidebarItem>
                <SidebarItem to="/logout" icon={faSignOut}>
                    Log out
                </SidebarItem>
            </Menu.Section>
        </Menu>
    );
};

export default Sidebar;
